<script setup lang="ts">
import { inject, onMounted, ref, watch } from 'vue';
import type { Ref } from 'vue';
import { DtInput } from '@dialpad/dialtone/vue3/lib/input';
import { DtValidationMessages } from '@dialpad/dialtone/vue3/lib/validation-messages';
import { DATA_QA } from './date-time.constants';
import logger from '@/logger';
import { TRIGGER_FORM_VALIDATION } from '@/app/utils/keys';
import { PHONE_EDIT_VALIDATION_ERROR } from '@/phone/PhoneEdit/phone-edit.constants';

const log = logger.getChild('DateTime');
log.debug('Mounting');

onMounted(() => {
  log.debug('Mounted');
});

type Props = {
  dateLabel?: string;
  timeLabel?: string;
  required?: boolean;
  disabled?: boolean;
};

const props = defineProps<Props>();

const model = defineModel<Date | null>();
const localModel = ref<Date | null>(null);

const dateString = ref('');
const timeString = ref('');

const updateDateString = () => {
  const date = localModel.value;
  if (!date) {
    dateString.value = '';
    timeString.value = '';
    return;
  }

  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  dateString.value = `${year}-${month}-${day}`;
  timeString.value = `${hours}:${minutes}`;
};

watch(
  () => model.value,
  (value) => {
    if (localModel.value === value) return;

    log.debug('Model changed -> changing local model to', value);
    localModel.value = value ?? null;
    updateDateString();
  },
  { immediate: true },
);

watch(
  () => localModel.value,
  (value) => {
    if (model.value === value) return;

    log.debug('Local model changed -> changing model to', value);
    model.value = value ?? null;
  },
);

watch([dateString, timeString], () => {
  if (!dateString.value || !timeString.value) {
    log.debug('Date string or time string is empty');
    localModel.value = null;
    return;
  }
  log.debug('Date string or time string changed');
  const date = localModel.value ? new Date(localModel.value) : new Date();
  const [year, month, day] = dateString.value.split('-').map(Number);
  const [hours, minutes] = timeString.value.split(':').map(Number);
  date.setFullYear(year, month - 1, day);
  date.setHours(hours, minutes);
  error.value = null;
  localModel.value = date;
});

const validationTrigger = inject<Ref<number>>(TRIGGER_FORM_VALIDATION, ref(0));

const emit =
  defineEmits<
    (e: 'update:error', value: PHONE_EDIT_VALIDATION_ERROR | null) => void
  >();

const error = ref<PHONE_EDIT_VALIDATION_ERROR | null>(null);

const validateValue = () => {
  if (dateString.value.length === 0 && timeString.value.length === 0) {
    if (!props.required) return;
    error.value = PHONE_EDIT_VALIDATION_ERROR.REQUIRED;
    return;
  } else if (dateString.value.length === 0 || timeString.value.length === 0) {
    error.value = PHONE_EDIT_VALIDATION_ERROR.INCORRECT_VALUE;
    return;
  }
};

watch(error, (value) => {
  emit('update:error', value);
});

watch([validationTrigger], () => {
  log.debug('Validation trigger');
  validateValue();
});
</script>

<template>
  <div :data-qa="DATA_QA.ROOT">
    <div class="d-d-flex d-ai-flex-end d-g16">
      <DtInput
        v-model="dateString"
        :input-wrapper-class="{
          'd-input--error': error && !dateString,
        }"
        :data-qa="DATA_QA.DATE_INPUT"
        :label="dateLabel"
        :disabled="disabled"
        type="date"
      />
      <DtInput
        v-model="timeString"
        :input-wrapper-class="{
          'd-input--error': error && !timeString,
        }"
        :data-qa="DATA_QA.TIME_INPUT"
        :label="timeLabel"
        :disabled="disabled"
        type="time"
      />
    </div>
    <DtValidationMessages
      v-if="error"
      :validation-messages="[
        {
          message: $ta('DATE_TIME_ERROR')[error],
          type: 'error',
        },
      ]"
    />
  </div>
</template>
